// src/components/exam-player/renderers/OrderingWeightedRenderer.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { X, RotateCcw } from "lucide-react";

/* -------------------- HOISTED, MEMOIZED SUBCOMPONENTS -------------------- */

const StemView = React.memo(function StemView({ html, isRtl }) {
  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="prose max-w-none text-gray-800 mb-3"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

const SlotsView = React.memo(function SlotsView({
  order,
  t,
  allowDrop,
  onDropToSlot,
  onDragStartItem,
  labelOf,
  clearSlot,
}) {
  return (
    <div className="grid gap-3">
      {order.map((id, idx) => (
        <div
          key={idx}
          onDragOver={allowDrop}
          onDrop={(e) => onDropToSlot(e, idx)}
          className="relative rounded-lg border-2 border-dashed border-gray-300 min-h-[64px] p-3 flex items-center justify-between bg-white"
        >
          <div className="text-xs text-gray-500 absolute -top-2 left-3 bg-white px-1">
            {t("ordering_weighted.slot_n", {
              defaultValue: "Slot {{n}}",
              n: idx + 1,
            })}
          </div>

          {id ? (
            <div
              draggable
              onDragStart={(e) => onDragStartItem(e, id)}
              className="w-full inline-flex items-center justify-between gap-2 rounded-md border px-3 py-2 bg-gray-50 cursor-move"
            >
              <span className="text-sm">{labelOf(id)}</span>
              <button
                type="button"
                onClick={() => clearSlot(idx)}
                className="p-1 rounded hover:bg-gray-200"
                aria-label={t("common.remove")}
                title={t("common.remove")}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-400">
              {t("ordering_weighted.drop_here", "Drop an item here")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

const PoolView = React.memo(function PoolView({
  pool,
  t,
  allowDrop,
  onDropBackToPool,
  onDragStartItem,
  labelOf,
  resetAll,
}) {
  return (
    <div
      onDragOver={allowDrop}
      onDrop={onDropBackToPool}
      className="rounded-lg border p-3 bg-gray-50"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-gray-600">
          {t("ordering_weighted.answers", "Answers")}
        </div>
        <button
          type="button"
          onClick={resetAll}
          className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800"
        >
          <RotateCcw size={14} />
          {t("common.reset", "Reset")}
        </button>
      </div>

      {pool.length === 0 ? (
        <div className="text-sm text-gray-400">
          {t("ordering_weighted.no_items", "No items in pool")}
        </div>
      ) : (
        <div className="grid gap-2 grid-cols-2">
          {pool.map((id) => (
            <div
              key={id}
              draggable
              onDragStart={(e) => onDragStartItem(e, id)}
              className="inline-flex items-center gap-2 rounded-md border bg-white px-3 py-2 cursor-move"
              title={labelOf(id)}
            >
              <span className="text-base">{labelOf(id)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const TwoPane = React.memo(function TwoPane({ left, right, reverse = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {reverse ? (
        <>
          <div>{right}</div>
          <div>{left}</div>
        </>
      ) : (
        <>
          <div>{left}</div>
          <div>{right}</div>
        </>
      )}
    </div>
  );
});

/* -------------------- MAIN COMPONENT -------------------- */

export default function OrderingWeightedRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const layout = item?.body?.layout || "top-bottom";
  const allItems = Array.isArray(item?.body?.items) ? item.body.items : [];

  // Normalize items (پایدار)
  const normalizedItems = useMemo(() => {
    return allItems.map((it, idx) => {
      const id = it?._id ?? String(idx);
      const labelText =
        it?.label?.fa?.trim?.() ||
        it?.label?.en?.trim?.() ||
        `${t("ordering_weighted.item", "Item")} ${idx + 1}`;
      return { ...it, _id: id, labelText };
    });
  }, [allItems, t]);

  const nonDistractors = useMemo(
    () => normalizedItems.filter((it) => !it.isDistractor),
    [normalizedItems]
  );

  const slotsCount = useMemo(() => {
    const maxRank = nonDistractors.reduce(
      (mx, it) => Math.max(mx, Number(it.targetIndex) || 0),
      0
    );
    return maxRank > 0 ? maxRank : nonDistractors.length || 0;
  }, [nonDistractors]);

  // Initial builders
  const buildInitialOrder = useCallback(
    (initAns) => {
      const ids = new Set(normalizedItems.map((x) => x._id));
      const fromInit =
        Array.isArray(initAns?.order) && initAns.order.length > 0
          ? initAns.order.filter((id) => id == null || ids.has(id))
          : [];
      const arr = new Array(Math.max(0, slotsCount)).fill(null);
      for (let i = 0; i < Math.min(arr.length, fromInit.length); i++) {
        arr[i] = fromInit[i] ?? null;
      }
      return arr;
    },
    [normalizedItems, slotsCount]
  );

  const buildInitialPool = useCallback(
    (initAns, initialOrder) => {
      const placed = new Set(initialOrder.filter(Boolean));
      if (Array.isArray(initAns?.unplaced) && initAns.unplaced.length > 0) {
        const ids = new Set(normalizedItems.map((x) => x._id));
        return initAns.unplaced.filter((id) => ids.has(id) && !placed.has(id));
      }
      return normalizedItems.map((x) => x._id).filter((id) => !placed.has(id));
    },
    [normalizedItems]
  );

  // Map & label
  const byId = useMemo(() => {
    const m = new Map();
    normalizedItems.forEach((it) => m.set(it._id, it));
    return m;
  }, [normalizedItems]);
  const labelOf = useCallback((id) => byId.get(id)?.labelText || "", [byId]);

  // Local state (uncontrolled)
  const initialOrder = useMemo(
    () => buildInitialOrder(initialAnswers),
    [buildInitialOrder, initialAnswers]
  );
  const initialPool = useMemo(
    () => buildInitialPool(initialAnswers, initialOrder),
    [buildInitialPool, initialAnswers, initialOrder]
  );

  const [order, setOrder] = useState(initialOrder);
  const [pool, setPool] = useState(initialPool);

  // Re-sync ONLY when the question changes (جلوگیری از remount/loop)
  const lastItemIdRef = useRef(item?._id ?? null);
  useEffect(() => {
    const curr = item?._id ?? null;
    if (curr !== lastItemIdRef.current) {
      setOrder(initialOrder);
      setPool(initialPool);
      lastItemIdRef.current = curr;
    }
  }, [item?._id, initialOrder, initialPool]);

  // Notify parent (بدون وابستگی به تغییر رفرنس تابع)
  const onAnswerChangeRef = useRef(onAnswerChange);
  useEffect(() => {
    onAnswerChangeRef.current = onAnswerChange;
  }, [onAnswerChange]);
  useEffect(() => {
    onAnswerChangeRef.current?.({ order, unplaced: pool });
  }, [order, pool]);

  /* -------------------- DnD -------------------- */

  const onDragStartItem = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.setData("text", id);
    e.dataTransfer.effectAllowed = "move";
  };

  const allowDrop = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const removeFromOrder = useCallback((id) => {
    setOrder((prev) => prev.map((x) => (x === id ? null : x)));
  }, []);

  const putInSlot = (slotIndex, id) => {
    setOrder((prevOrder) => {
      const nextOrder = [...prevOrder];
      const prevIndex = nextOrder.findIndex((x) => x === id);
      if (prevIndex >= 0) nextOrder[prevIndex] = null;

      const replaced = nextOrder[slotIndex];
      nextOrder[slotIndex] = id;

      setPool((prevPool) => {
        const withoutId = prevPool.filter((x) => x !== id);
        return replaced && !withoutId.includes(replaced)
          ? [...withoutId, replaced]
          : withoutId;
      });

      return nextOrder;
    });
  };

  const onDropToSlot = (e, slotIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const id =
      e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text");
    if (!id) return;
    setPool((p) => p.filter((x) => x !== id));
    putInSlot(slotIndex, id);
  };

  const onDropBackToPool = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const id =
      e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text");
    if (!id) return;
    removeFromOrder(id);
    setPool((p) => (p.includes(id) ? p : [...p, id]));
  };

  const clearSlot = (slotIndex) => {
    setOrder((prev) => {
      const next = [...prev];
      const id = next[slotIndex];
      if (id) setPool((p) => (p.includes(id) ? p : [...p, id]));
      next[slotIndex] = null;
      return next;
    });
  };

  const resetAll = () => {
    setOrder(new Array(slotsCount).fill(null));
    setPool(normalizedItems.map((x) => x._id));
  };

  /* -------------------- LAYOUT -------------------- */

  const stemHtml = item?.stem?.fa || item?.stem?.en || "";

  let content = null;
  if (layout === "left-right") {
    content = (
      <TwoPane
        left={<StemView html={stemHtml} isRtl={isRtl} hidden={hideStem} />}
        right={
          <>
            <SlotsView
              order={order}
              t={t}
              allowDrop={allowDrop}
              onDropToSlot={onDropToSlot}
              onDragStartItem={onDragStartItem}
              labelOf={labelOf}
              clearSlot={clearSlot}
            />
            <div className="mt-4">
              <PoolView
                pool={pool}
                t={t}
                allowDrop={allowDrop}
                onDropBackToPool={onDropBackToPool}
                onDragStartItem={onDragStartItem}
                labelOf={labelOf}
                resetAll={resetAll}
              />
            </div>
          </>
        }
      />
    );
  } else if (layout === "right-left") {
    content = (
      <TwoPane
        reverse
        left={<StemView html={stemHtml} isRtl={isRtl} hidden={hideStem} />}
        right={
          <>
            <SlotsView
              order={order}
              t={t}
              allowDrop={allowDrop}
              onDropToSlot={onDropToSlot}
              onDragStartItem={onDragStartItem}
              labelOf={labelOf}
              clearSlot={clearSlot}
            />
            <div className="mt-4">
              <PoolView
                pool={pool}
                t={t}
                allowDrop={allowDrop}
                onDropBackToPool={onDropBackToPool}
                onDragStartItem={onDragStartItem}
                labelOf={labelOf}
                resetAll={resetAll}
              />
            </div>
          </>
        }
      />
    );
  } else if (layout === "bottom-top") {
    content = (
      <>
        <SlotsView
          order={order}
          t={t}
          allowDrop={allowDrop}
          onDropToSlot={onDropToSlot}
          onDragStartItem={onDragStartItem}
          labelOf={labelOf}
          clearSlot={clearSlot}
        />
        <div className="mt-4">
          <PoolView
            pool={pool}
            t={t}
            allowDrop={allowDrop}
            onDropBackToPool={onDropBackToPool}
            onDragStartItem={onDragStartItem}
            labelOf={labelOf}
            resetAll={resetAll}
          />
        </div>
        {!hideStem && (
          <div className="mt-6">
            <StemView html={stemHtml} isRtl={isRtl} hidden={false} />
          </div>
        )}
      </>
    );
  } else {
    // top-bottom (default)
    content = (
      <>
        <StemView html={stemHtml} isRtl={isRtl} hidden={hideStem} />
        <SlotsView
          order={order}
          t={t}
          allowDrop={allowDrop}
          onDropToSlot={onDropToSlot}
          onDragStartItem={onDragStartItem}
          labelOf={labelOf}
          clearSlot={clearSlot}
        />
        <div className="mt-4">
          <PoolView
            pool={pool}
            t={t}
            allowDrop={allowDrop}
            onDropBackToPool={onDropBackToPool}
            onDragStartItem={onDragStartItem}
            labelOf={labelOf}
            resetAll={resetAll}
          />
        </div>
      </>
    );
  }

  return (
    <div className="rounded-lg bg-white p-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        {!hideStem && (
          <div className="text-xs text-gray-500">
            {t("preview.question_n", "Question")} {questionNumber}
          </div>
        )}
        <div className="text-xs text-gray-500">
          {t("ordering_weighted.slots_hint", {
            defaultValue: "Fill all slots in order.",
          })}
        </div>
      </div>
      {content}
    </div>
  );
}

/* نکات مهم:
 * 1) زیرکامپوننت‌ها بیرون و با React.memo تعریف شده‌ن ⇒ دیگر هر رندر Unmount/Mount نمی‌شوند.
 * 2) فقط وقتی item._id عوض شود، state از initialAnswers ست می‌شود ⇒ لوپ و “پرش” نداریم.
 * 3) اگر در Dev از React.StrictMode استفاده می‌کنی، توجه کن که رندر/افکت‌ها دوبار اجرا می‌شوند
 *    (برای تست «پرش»، موقتاً StrictMode را بردار تا مطمئن شوی مشکل از remount نبود).
 */
